import React, { useLayoutEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  SELLER_APP_QR_URL,
  sellerAppStoreAnchorProps,
  sellerPlayStoreAnchorProps,
} from '../constants/appLinks';

const steps = [
  { icon: 'ri-smartphone-line', label: 'Download the app', sub: 'Free on iOS and Android' },
  { icon: 'ri-store-line', label: 'Create your store', sub: 'Add listings and delivery options' },
  { icon: 'ri-wallet-3-line', label: 'Deliver and get paid', sub: 'Stripe handles card payments' },
];

const heroScreens = [
  { src: '/sell/screen-orders.png', alt: 'My orders in BuyDrop Seller' },
  { src: '/sell/screen-delivery.png', alt: 'Out for delivery in BuyDrop Seller' },
  { src: '/sell/screen-earnings.png', alt: 'Earnings in BuyDrop Seller' },
];

function scrollToHash(hash: string) {
  if (!hash || hash === '#') return;
  const id = hash.replace(/^#/, '');
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function PhonePreview({ src, alt, className = '' }: { src: string; alt: string; className?: string }) {
  return (
    <div
      className={`shrink-0 rounded-[14px] border-[3px] border-gray-900 bg-gray-900 shadow-lg ${className}`}
    >
      <img
        src={src}
        alt={alt}
        className="block w-full h-auto rounded-[11px]"
        loading="lazy"
        decoding="async"
      />
    </div>
  );
}

const SellOnApp: React.FC = () => {
  const location = useLocation();

  useLayoutEffect(() => {
    scrollToHash(location.hash);
  }, [location.pathname, location.hash]);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative border-b border-gray-100 bg-gradient-to-b from-green-50/70 to-white">
        <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-16 lg:py-24 overflow-visible">
          <div className="grid items-center gap-8 lg:grid-cols-[1fr_1.05fr] lg:gap-12 overflow-visible">
            <div>
              <p className="flex items-baseline gap-x-1 text-lg sm:text-xl lg:text-2xl font-bold tracking-tight">
                <span className="bg-gradient-to-r from-green-600 to-green-500 bg-clip-text text-transparent">
                  Buy
                </span>
                <span className="text-gray-900">Drop</span>
                <span className="ml-1.5 text-base sm:text-lg lg:text-xl font-semibold text-gray-600">
                  Seller
                </span>
              </p>
              <h1 className="mt-3 sm:mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl lg:text-5xl lg:leading-[1.1]">
                Sell local.
                <br />
                Deliver. Get paid.
              </h1>
              <p className="mt-4 sm:mt-5 max-w-md text-sm sm:text-base leading-relaxed text-gray-600">
                Listings, orders, navigation, and payouts in one app for neighborhood sellers and
                deliverers.
              </p>

              <div
                className="mt-6 flex justify-center items-center gap-2 sm:gap-3 lg:hidden"
                aria-hidden
              >
                <PhonePreview
                  src={heroScreens[0].src}
                  alt=""
                  className="w-[76px] sm:w-[88px] shrink-0 -rotate-6 opacity-95"
                />
                <PhonePreview
                  src={heroScreens[1].src}
                  alt=""
                  className="w-[88px] sm:w-[100px] shrink-0 z-10"
                />
                <PhonePreview
                  src={heroScreens[2].src}
                  alt=""
                  className="w-[76px] sm:w-[88px] shrink-0 rotate-6 opacity-95"
                />
              </div>

              <div id="seller-download" className="scroll-mt-24 mt-6 sm:mt-8 space-y-4 sm:space-y-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                  <a
                    {...sellerAppStoreAnchorProps()}
                    className="inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-xl bg-gray-900 px-5 py-3.5 text-white transition hover:bg-gray-800 sm:min-w-[168px] sm:justify-start"
                    aria-label="Download BuyDrop Seller on the App Store"
                  >
                    <i className="ri-apple-fill text-2xl" aria-hidden />
                    <span className="text-left leading-tight">
                      <span className="block text-[10px] font-normal text-white/70 uppercase tracking-wide">
                        Download on the
                      </span>
                      <span className="block text-sm font-semibold">App Store</span>
                    </span>
                  </a>
                  <a
                    {...sellerPlayStoreAnchorProps()}
                    className="inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-xl bg-white px-5 py-3.5 text-gray-900 ring-1 ring-gray-300 transition hover:bg-gray-50 sm:min-w-[168px] sm:justify-start"
                    aria-label="Get BuyDrop Seller on Google Play"
                  >
                    <i className="ri-google-play-fill text-2xl text-green-600" aria-hidden />
                    <span className="text-left leading-tight">
                      <span className="block text-[10px] font-normal text-gray-500 uppercase tracking-wide">
                        Get it on
                      </span>
                      <span className="block text-sm font-semibold">Google Play</span>
                    </span>
                  </a>
                </div>
                <div className="hidden sm:flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="rounded-lg bg-white p-1.5 ring-1 ring-gray-100 shrink-0">
                    <QRCodeSVG
                      value={SELLER_APP_QR_URL}
                      size={76}
                      level="M"
                      marginSize={2}
                      title="Scan to download BuyDrop Seller"
                    />
                  </div>
                  <p className="text-sm leading-relaxed text-gray-500">
                    Scan to open the App Store or Google Play on your phone.
                  </p>
                </div>
              </div>
            </div>

            <div className="hidden lg:flex justify-end overflow-visible">
              <div className="flex items-center gap-3 overflow-visible">
                <PhonePreview
                  src={heroScreens[0].src}
                  alt={heroScreens[0].alt}
                  className="w-[130px] shrink-0 -rotate-6 opacity-95"
                />
                <PhonePreview
                  src={heroScreens[1].src}
                  alt={heroScreens[1].alt}
                  className="w-[150px] shrink-0 z-10"
                />
                <PhonePreview
                  src={heroScreens[2].src}
                  alt={heroScreens[2].alt}
                  className="w-[130px] shrink-0 rotate-6 opacity-95"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="scroll-mt-24 border-b border-gray-100 bg-gray-50/60">
        <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16 lg:py-20">
          <div className="max-w-lg mb-8 sm:mb-10">
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-gray-900">How it works</h2>
            <p className="mt-2 text-sm leading-relaxed text-gray-600">
              Three steps from download to payout.{' '}
              <Link to="/help" className="font-medium text-green-700 hover:underline">
                Help center
              </Link>
            </p>
          </div>
          <ol className="grid gap-4 md:grid-cols-3 md:gap-5">
            {steps.map((step, i) => (
              <li
                key={step.label}
                className="rounded-xl border border-gray-200 bg-white p-5 sm:p-6 shadow-sm"
              >
                <div className="flex items-center gap-3">
                  <span
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-green-600 text-white shrink-0"
                    aria-hidden
                  >
                    <i className={`${step.icon} text-base`} />
                  </span>
                  <span className="text-xs font-semibold uppercase tracking-wide text-green-700">
                    Step {i + 1}
                  </span>
                </div>
                <h3 className="mt-4 text-base font-semibold text-gray-900">{step.label}</h3>
                <p className="mt-1 text-sm leading-relaxed text-gray-600">{step.sub}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <div className="border-t border-gray-100 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10">
          <p className="text-sm text-gray-500 text-center sm:text-left">
          Looking to buy?{' '}
          <Link to="/" className="font-medium text-gray-900 hover:underline">
            Browse listings
          </Link>
          {' · '}
          <Link to="/login" className="font-medium text-gray-900 hover:underline">
            Log in
          </Link>
        </p>
        </div>
      </div>
    </div>
  );
};

export default SellOnApp;
